import { Link, Navigate } from "react-router-dom";
import { ArrowRight, MapPin, ShieldCheck, Factory } from "lucide-react";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { USA_HUB, USA_COMMERCIAL_PAGES } from "@/data/usaPages";
import { US_STATES } from "@/data/usaStates";

/**
 * /usa — Canonical US-buyer hub page.
 *
 * All content is unique to this hub (different search intent from
 * /regions/usa which now 301-redirects here). Uses the site's warm
 * paper/ink editorial palette to match interior editorial routes.
 */
export const Usa = () => {
  const data = USA_HUB;
  const { open } = useStartProject();

  useSeo({
    title: data.title,
    description: data.metaDescription,
    path: data.slug,
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          "@id": `${SITE_URL}${data.slug}#service`,
          name: "India sourcing & quality control for US buyers",
          description: data.metaDescription,
          provider: { "@id": `${SITE_URL}#organization` },
          areaServed: { "@type": "Country", name: "United States" },
          serviceType:
            "India sourcing agent · India buying agent · India quality control · Factory inspection · Supplier verification · Product development",
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "US-buyer commercial programmes",
            itemListElement: USA_COMMERCIAL_PAGES.map((p, i) => ({
              "@type": "Offer",
              position: i + 1,
              itemOffered: {
                "@type": "Service",
                name: p.h1,
                url: `${SITE_URL}${p.slug}`,
              },
            })),
          },
        },
        {
          "@type": "FAQPage",
          "@id": `${SITE_URL}${data.slug}#faq`,
          mainEntity: (data.faqs || []).map(([q, a]) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        },
        breadcrumb([
          { name: "Home", path: "/" },
          { name: "USA", path: data.slug },
        ]),
      ],
    },
    jsonLdId: "usa-hub-jsonld",
  });

  return (
    <article data-testid="usa-hub-page">
      {/* HERO */}
      <header
        className="wrap"
        style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}
      >
        <Reveal
          as="nav"
          aria-label="Breadcrumbs"
          style={{ marginBottom: "1.2rem" }}
        >
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              color: "var(--brass-deep)",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            <Link to="/" style={{ color: "inherit" }}>
              Home
            </Link>{" "}
            · USA
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="usa-eyebrow">
            <span
              aria-hidden="true"
              style={{
                marginRight: ".5rem",
                fontSize: "1.4em",
                verticalAlign: "middle",
              }}
            >
              🇺🇸
            </span>
            {data.eyebrow}
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text={data.h1}
          className="display tp-title"
          stagger={70}
          testId="usa-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            maxWidth: "72ch",
            color: "var(--ink-soft)",
            fontSize: "1.1rem",
            marginTop: "1.4rem",
            lineHeight: 1.65,
          }}
        >
          {data.lede}
        </Reveal>
        <Reveal
          as="div"
          delay={280}
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: ".8rem",
            marginTop: "2rem",
          }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="usa-cta-primary"
          >
            Start Your Project →
          </button>
          <Link to="/ask" className="btn btn-ghost" data-testid="usa-cta-ask">
            Read the US buyer FAQ
          </Link>
        </Reveal>
      </header>

      {/* SIX COMMERCIAL PAGES — the key US programmes */}
      <section
        className="wrap"
        style={{
          paddingBlock: "clamp(2.4rem, 5vw, 4rem)",
          borderTop: "1px solid var(--line)",
        }}
        data-testid="usa-programmes"
      >
        <Reveal as="p" className="eyebrow">
          Six US-buyer programmes
        </Reveal>
        <Reveal
          as="h2"
          className="display"
          style={{ margin: "1rem 0 2rem", maxWidth: "36ch" }}
        >
          Choose the exact commercial engagement your team needs.
        </Reveal>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: "1rem",
          }}
        >
          {USA_COMMERCIAL_PAGES.map((p) => (
            <Link
              key={p.id}
              to={p.slug}
              style={{
                display: "block",
                padding: "1.4rem",
                border: "1px solid var(--line)",
                textDecoration: "none",
                color: "inherit",
                background: "var(--paper-raised)",
              }}
              data-testid={`usa-programme-${p.id}`}
            >
              <p
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".66rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  margin: 0,
                }}
              >
                US programme
              </p>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontSize: "1.15rem",
                  margin: ".5rem 0 .4rem",
                  letterSpacing: "-.005em",
                  lineHeight: 1.3,
                }}
              >
                {p.h1.split("—")[0].trim()}
              </p>
              <p
                style={{
                  color: "var(--ink-soft)",
                  fontSize: ".92rem",
                  lineHeight: 1.55,
                  margin: 0,
                }}
              >
                {p.metaDescription.slice(0, 140)}…
              </p>
              <p
                className="mono"
                style={{
                  marginTop: "1rem",
                  fontSize: ".68rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  color: "var(--brass-deep)",
                }}
              >
                Read programme →
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* BODY sections */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section) 0", maxWidth: "1200px" }}
        data-testid="usa-states"
      >
        <h2
          className="display"
          style={{
            fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
            margin: "0 0 .8rem",
            letterSpacing: "-.01em",
            lineHeight: 1.15,
          }}
        >
          US state programmes
        </h2>
        <p
          style={{
            color: "var(--ink-soft)",
            fontSize: "1rem",
            lineHeight: 1.7,
            margin: "0 0 1.6rem",
            maxWidth: "70ch",
          }}
        >
          Eight state-specific India sourcing programmes for US buyers. Each page details the port lane, buyer profiles, compliance stack and operational cadence relevant to that state.
        </p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "0.9rem",
          }}
        >
          {US_STATES.map((s) => (
            <Link
              key={s.slug}
              to={s.slug}
              data-testid={`usa-state-link-${s.stateShort.toLowerCase()}`}
              style={{
                display: "block",
                padding: "1.1rem 1.2rem",
                border: "1px solid var(--line)",
                color: "var(--ink)",
                textDecoration: "none",
                background: "var(--paper)",
                transition: "border-color 200ms",
              }}
            >
              <p
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".62rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  margin: "0 0 .3rem",
                }}
              >
                State programme · {s.stateShort}
              </p>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontSize: "1.05rem",
                  letterSpacing: "-.005em",
                  margin: 0,
                }}
              >
                India sourcing · {s.state} →
              </p>
            </Link>
          ))}
        </div>
      </section>

      {/* BODY sections */}
      <section
        className="wrap"
        style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}
      >
        {(data.sections || []).map((s, i) => (
          <Reveal
            key={i}
            as="section"
            style={{ marginTop: i === 0 ? 0 : "2.6rem" }}
          >
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.1rem",
                letterSpacing: "-.01em",
                lineHeight: 1.15,
              }}
            >
              {s.h2}
            </h2>
            {(s.body || []).map((para, ix) => (
              <p
                key={ix}
                style={{
                  color: "var(--ink-soft)",
                  fontSize: "1.04rem",
                  lineHeight: 1.75,
                  margin: "0 0 1.1rem",
                  maxWidth: "68ch",
                }}
              >
                {para}
              </p>
            ))}
            {s.subsections && (
              <div style={{ marginTop: "1rem" }}>
                {s.subsections.map((ss, ix) => (
                  <div
                    key={ix}
                    style={{
                      borderTop: "1px solid var(--line)",
                      paddingBlock: "1rem",
                    }}
                  >
                    <h3
                      style={{
                        fontFamily: "var(--display)",
                        fontSize: "1.05rem",
                        margin: "0 0 .3rem",
                        letterSpacing: "-.005em",
                      }}
                    >
                      {ss.h3}
                    </h3>
                    <p
                      style={{
                        color: "var(--ink-soft)",
                        fontSize: ".98rem",
                        lineHeight: 1.65,
                        margin: 0,
                        maxWidth: "68ch",
                      }}
                    >
                      {ss.body}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </Reveal>
        ))}
      </section>

      {/* TRUST STRIP */}
      <section
        className="wrap"
        style={{
          paddingBlock: "clamp(2rem, 4vw, 3.4rem)",
          borderBlock: "1px solid var(--line)",
        }}
        data-testid="usa-trust"
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "1.6rem 2rem",
          }}
        >
          {[
            { icon: Factory, label: "Vetted factories", value: "1,000+" },
            { icon: MapPin, label: "Manufacturing clusters", value: "15" },
            { icon: ShieldCheck, label: "AQL inspection default", value: "2.5 · 4.0" },
            { icon: Factory, label: "US share of shipments", value: "≈ 40%" },
          ].map(({ icon: Icon, label, value }) => (
            <Reveal key={label}>
              <Icon
                size={18}
                strokeWidth={1.3}
                aria-hidden
                style={{ color: "var(--brass-deep)" }}
              />
              <span
                className="mono"
                style={{
                  display: "block",
                  color: "var(--brass-deep)",
                  fontSize: ".68rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                  marginTop: ".5rem",
                }}
              >
                {label}
              </span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.35rem",
                  margin: ".2rem 0 0",
                  letterSpacing: "-.005em",
                }}
              >
                {value}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section
        className="wrap"
        style={{
          paddingBlock: "var(--section)",
          maxWidth: "820px",
        }}
        data-testid="usa-faq"
      >
        <Reveal as="p" className="eyebrow">
          Frequently asked · US buyers
        </Reveal>
        <Reveal
          as="h2"
          className="display"
          style={{ margin: "1rem 0 2rem" }}
        >
          Questions from US buyers we hear every week.
        </Reveal>
        {data.faqs.map(([q, a], i) => (
          <Reveal
            key={i}
            as="details"
            data-testid={`usa-faq-${i}`}
            style={{
              borderBottom: "1px solid var(--line)",
              padding: "1.4rem 0",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontFamily: "var(--display)",
                fontSize: "1.1rem",
                color: "var(--ink)",
                listStyle: "none",
              }}
            >
              {q}
            </summary>
            <p
              style={{
                marginTop: ".8rem",
                color: "var(--ink-soft)",
                lineHeight: 1.7,
                maxWidth: "68ch",
              }}
            >
              {a}
            </p>
          </Reveal>
        ))}
      </section>

      {/* CTA */}
      <section
        className="wrap"
        style={{
          paddingBlock: "var(--section)",
          borderTop: "1px solid var(--line)",
        }}
      >
        <Reveal as="p" className="eyebrow">
          Start a US programme
        </Reveal>
        <Reveal as="h2" className="display" style={{ maxWidth: "34ch" }}>
          {data.ctaHeadline}
        </Reveal>
        <Reveal
          as="div"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "1rem",
            marginTop: "2rem",
          }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="usa-cta-start-project"
          >
            {data.primaryCta.label} <ArrowRight size={16} strokeWidth={1.6} aria-hidden />
          </button>
          <Link
            to={data.secondaryCta.href}
            className="btn btn-ghost"
            data-testid="usa-cta-contact"
          >
            {data.secondaryCta.label}
          </Link>
        </Reveal>
      </section>
    </article>
  );
};

/**
 * Redirect helper for /regions/usa → /usa. Kept as a React-level fallback
 * behind the edge-level 301 in /public/_redirects.
 */
export const UsaLegacyRedirect = () => <Navigate to="/usa" replace />;

export default Usa;
